import { makeAutoObservable } from "mobx";
import { inject, injectable } from "tsyringe";
import { ProjectBusEvents, TOKEN_IProjectBus, type IProjectBus } from "@/services/ProjectBus";
import ExistingShareLinkStore from "../transient/ExistingShareLinkStore";
import { ShareLink } from "@/ProjectWorld";
import { EntityCollection } from "@utilitydelta/clientside-event-sourcing";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "@/services/ProjectWorldReader";
import { TOKEN_IUserIdentity, type IUserIdentity } from "@/services/UserIdentity";
import { TOKEN_ITransientLocator, type ITransientLocator } from "@/services/TransientLocator";
import { TOKEN_IWrapAuth0, type IWrapAuth0 } from "@/services/WrapAuth0";

export const TOKEN_ShareLinksDialogStore = Symbol("ShareLinksDialogStore");

@injectable()
export class ShareLinksDialogStore {
    private _shareLinkIdMap: Map<string, ExistingShareLinkStore> = new Map();
    private _shareLinkStores: ExistingShareLinkStore[] = [];
    private _currentUserId: string = '';
    private _projectId: string | undefined;

    constructor(
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
        @inject(TOKEN_ITransientLocator) private readonly transientLocator: ITransientLocator,
        @inject(TOKEN_IProjectWorldReader) private readonly projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_IUserIdentity) private readonly userIdentity: IUserIdentity,
        @inject(TOKEN_IWrapAuth0) private readonly wrapAuth0: IWrapAuth0,
    ) {
        makeAutoObservable(this);

        projectBus.subscribe_projectLoaded(this.onProjectLoaded);
        projectBus.subscribe_projectionUpdated(this.onProjectionUpdated);
        projectBus.subscribe_authenticationChanged(this.onAuthenticationChanged);
    }
    onAuthenticationChanged = async (data: ProjectBusEvents['AUTHENTICATION_CHANGED']) => {
        this._currentUserId = data.isAuthenticated ? data.user!.sub! : (await this.userIdentity.publicKey()).publicKeyHashed;
    }

    private onProjectionUpdated = async (data: ProjectBusEvents['PROJECTION_UPDATED']): Promise<void> => {
        if (!this._shareLinkStores) return;

        if (data.mutationResult.kind === "AddShareLink_ProjectionResult") {
            this.getOrCreateStore(data.mutationResult.shareLink);
            this.reSort();
        }

        if (data.mutationResult.kind === 'DisableShareLink_ProjectionResult') {
            const store = this._shareLinkIdMap.get(data.mutationResult.shareLink.id);
            if (store) {
                store.isActive = false;
            }
        }

        if (data.mutationResult.kind === 'ProvideAccess_ProjectionResult') {
            if (data.mutationResult.shareLink) {
                const store = this._shareLinkIdMap.get(data.mutationResult.shareLink.id);
                if (store) {
                    store.usageCount = data.mutationResult.shareLink.usageCount ?? 0;
                }
            }
        }
    }

    private getOrCreateStore(shareLink: ShareLink): ExistingShareLinkStore {
        let shareLinkStore = this._shareLinkIdMap.get(shareLink.id);
        if (!shareLinkStore) {
            shareLinkStore = this.transientLocator.resolve<ExistingShareLinkStore>(ExistingShareLinkStore);
            shareLinkStore.init(shareLink, this._currentUserId);
            this._shareLinkStores.push(shareLinkStore);
            this._shareLinkIdMap.set(shareLinkStore.id, shareLinkStore);
        }
        return shareLinkStore;
    }

    private onProjectLoaded = async (data: ProjectBusEvents['PROJECT_LOADED']): Promise<void> => {
        if (this._projectId === data.projectId) return;
        const projectWorld = await this.projectWorldReader.mustGet(data.projectId);
        this._currentUserId = this.wrapAuth0.state.isAuthenticated ? this.wrapAuth0.userIdHashed! : (await this.userIdentity.publicKey()).publicKeyHashed;
        this._shareLinkStores = this.buildShareLinkStores(this._currentUserId, projectWorld.shareLinks);
        this.reSort();
        this._shareLinkIdMap = new Map(this._shareLinkStores.map(shareLinkStore => [shareLinkStore.id, shareLinkStore]));
    }

    private buildShareLinkStores(currentUserId: string, shareLinks: EntityCollection<ShareLink>): ExistingShareLinkStore[] {
        return shareLinks.allNotDeleted.map(shareLink => {
            const store = this.transientLocator.resolve<ExistingShareLinkStore>(ExistingShareLinkStore);
            store.init(shareLink, currentUserId);
            return store;
        });
    }

    public get shareLinkStores(): ExistingShareLinkStore[] {
        return this._shareLinkStores;
    }

    private reSort() {
        this._shareLinkStores = this._shareLinkStores.sort((a, b) => {
            // createdOn descending
            return b.createdOn - a.createdOn;
        });
    }
}